import type { PossibleDuJour, TacheDeJournee } from '../../partage/api/types';
import { faitesDeLaJournee, garderLesPossibles } from './journeeAChanger';
import { choixDeLaJournee, type Choix } from './journeeEnCours';

/**
 * La journée en cours au téléphone (chantier `telephone-journee-agenda`, ticket 04, décisions
 * d'Igor du 2026-10-05, 7, 8, 11 et 20 ; maquette `design/telephone.html`, piste « Journée »,
 * « La journée en cours »). Sans rendu.
 *
 * Le serveur range la journée et donne l'heure qu'il est chez l'utilisateur ; ici, seulement ce
 * qui se montre : la tâche de l'heure, les lignes dans l'ordre des heures avec les rendez-vous du
 * jour, le compte, et ce qu'on renvoie au serveur pour « Changer ma journée ». « Plus tard
 * aujourd'hui » se fait au serveur (`plusTardDansLaJournee`). La fin d'une tâche (son heure plus sa durée) se compte ici, pour le seul affichage.
 */

/** Combien de temps « Annuler » reste après une coche. */
export const DUREE_D_ANNULER_MS = 6000;

/** « 09:30 » en minutes depuis minuit ; des secondes éventuelles sont ignorées. */
export function minutesDuJour(heure: string): number {
  const [h = 0, m = 0] = heure.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
}

/**
 * L'heure dite par le serveur, avancée du temps passé depuis sa lecture : la tâche de l'heure
 * suit l'horloge sans relire la journée. Jamais au-delà de 23:59.
 */
export function heureAvancee(heure: string, minutesPassees: number): string {
  const total = Math.min(23 * 60 + 59, minutesDuJour(heure) + Math.max(0, Math.floor(minutesPassees)));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

type TacheLue =Pick<TacheDeJournee, 'bulleId' | 'etat' | 'heure' | 'dureeMin'>;

/** Comment la tâche de l'heure se présente : en cours, la prochaine, ou restée d'avant. */
export type QuandDeLHeure = 'en_ce_moment' | 'a_suivre' | 'a_faire';

/**
 * La tâche de l'heure, en grand : la première non faite qui n'est pas finie à cette heure (en
 * cours ou à venir). Si toutes celles qui restent sont passées, la première d'entre elles : elle
 * reste à faire, sans reproche. Rien qui reste : aucune.
 */
export function tacheDeLHeure(taches: readonly TacheLue[], heure: string): { id: string; quand: QuandDeLHeure } | null {
  const restent = taches.filter((t) => t.etat !== 'fait');
  if (restent.length === 0) return null;
  const maintenant = minutesDuJour(heure);
  const suivante = restent.find((t) => minutesDuJour(t.heure) + t.dureeMin > maintenant);
  if (!suivante) return { id: restent[0]!.bulleId, quand: 'a_faire' };
  return { id: suivante.bulleId, quand: minutesDuJour(suivante.heure) <= maintenant ? 'en_ce_moment' : 'a_suivre' };
}

/** Une ligne de la journée en cours : une tâche, ou un rendez-vous du jour à son heure. */
export type LigneDuMoment<T, R> = { sorte: 'tache'; tache: T } | { sorte: 'rendez_vous'; rendezVous: R };

/**
 * Les lignes dans l'ordre : les tâches dans celui de la journée (le serveur les a rangées), chaque
 * rendez-vous glissé avant la première tâche qui commence après lui ; à la même heure, le
 * rendez-vous d'abord.
 */
export function lignesDuMoment<T extends Pick<TacheDeJournee, 'heure'>, R extends { heure: string }>(
  taches: readonly T[],
  rendezVous: readonly R[]
): LigneDuMoment<T, R>[] {
  const restent = [...rendezVous].sort((a, b) => minutesDuJour(a.heure) - minutesDuJour(b.heure));
  const lignes: LigneDuMoment<T, R>[] = [];
  for (const t of taches) {
    while (restent.length > 0 && minutesDuJour(restent[0]!.heure) <= minutesDuJour(t.heure)) {
      lignes.push({ sorte: 'rendez_vous', rendezVous: restent.shift()! });
    }
    lignes.push({ sorte: 'tache', tache: t });
  }
  for (const r of restent) lignes.push({ sorte: 'rendez_vous', rendezVous: r });
  return lignes;
}

/** « 1 sur 3 faites · 1 h 40 prévues ». */
export function compteDuJour(taches: readonly TacheLue[]): { faites: number; total: number; minutes: number } {
  return {
    faites: taches.filter((t) => t.etat === 'fait').length,
    total: taches.length,
    minutes: taches.reduce((n, t) => n + t.dureeMin, 0),
  };
}

/** « Tout est fait » : une journée dont chaque tâche est cochée. */
export function toutEstFait(taches: readonly TacheLue[]): boolean {
  return taches.length > 0 && taches.every((t) => t.etat === 'fait');
}

/**
 * « Recommencer » garde ce qui est fait (spec `maintenant`, « Rouverte aujourd'hui, une journée
 * garde ses tâches faites ») : les tâches cochées de la journée, chacune fixée à son heure, que
 * « Propose-moi » et « Je choisis » renvoient avec la nouvelle journée.
 */
export function faitesGardees(taches: readonly TacheLue[]): Choix[] {
  return taches.filter((t) => t.etat === 'fait').map((t) => ({ id: t.bulleId, fixee: t.heure.slice(0, 5) }));
}

/** La journée envoyée au serveur : les faites gardées d'abord, puis les choisies, jamais deux fois. */
export function avecLesFaites(gardees: readonly Choix[], choisies: readonly Choix[]): Choix[] {
  const dedans = new Set(gardees.map((c) => c.id));
  return [...gardees, ...choisies.filter((c) => !dedans.has(c.id))];
}

/**
 * La journée qu'ouvre « Changer ma journée » : telle qu'enregistrée, ses heures fixées gardées ;
 * ce qui est fait reste, ce qui ne se fait plus aujourd'hui (bloqué depuis) part, comme dans le
 * panneau « Préparer ma journée ».
 */
export function choixAChanger(
  taches: readonly (TacheLue & { fixee?: boolean })[],
  possibles: readonly Pick<PossibleDuJour, 'id'>[]
): Choix[] {
  return garderLesPossibles(choixDeLaJournee(taches), possibles, faitesDeLaJournee({ taches }));
}
